import type { ActivityEntry, HistoryEvent, Priority } from './api';

export const PRIORITY_LABEL: Record<Priority, string> = { urgent: 'Urgent', today: 'Today', watch: 'Watching' };

/** "30 hours" / "2 days" / "5 minutes": how long ago, from a number of hours. */
export function formatDuration(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} minutes`;
  if (hours < 48) return `${Math.round(hours)} ${Math.round(hours) === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  return `${days} days`;
}

export function hoursSince(iso: string, now: number = Date.now()): number {
  return Math.max(0, (now - new Date(iso).getTime()) / 3600000);
}

/** An instant written in the company's zone when it has one confirmed, otherwise in UTC (never silently in the browser's zone). */
export function formatWhen(iso: string, timeZone: string | null): string {
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone || 'UTC', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? {} : { timeZoneName: 'short' }) });
}

const RESOLVED_REASON: Record<string, string> = {
  vendor_replied: 'The vendor replied',
  follow_up_sent: 'A follow-up was sent',
  no_longer_due: 'No longer overdue',
  no_longer_detected: 'No longer detected',
  comment_removed: 'The comment was removed',
};
export const resolvedReasonLabel = (reason: string | null) => (reason ? RESOLVED_REASON[reason] || reason.replace(/_/g, ' ') : 'Resolved');

/** The sentence a history entry reads as. */
export function eventText(event: Pick<HistoryEvent, 'event_type' | 'actor' | 'data'>, label?: string | null): string {
  const who = event.actor || 'Someone';
  const on = label ? ` on ${label}` : '';
  switch (event.event_type) {
    case 'detected': return `Bob found a comment${on} with no vendor reply`;
    case 'reopened': return `The vendor is still silent${on}: the situation was reopened`;
    case 'proposed': return 'A suggestion was prepared';
    case 'proposal_replaced':
      return event.data?.source === 'ai'
        ? `${who} gave a new instruction: the suggestion was replaced${event.data?.from_version ? ` (v${event.data.from_version} → v${event.data.version})` : ''}`
        : `The evidence changed: the suggestion was refreshed${event.data?.version ? ` (v${event.data.version})` : ''}`;
    case 'approved': return `${who} approved the suggestion${event.data?.edited ? ' (with edits)' : ''}`;
    case 'executed': return `The approved action ran${on}`;
    case 'failed': return `An approved action failed${on}`;
    case 'retry': return `${who} retried a failed action`;
    case 'dismissed': return `${who} dismissed it${event.data?.reason ? `: ${event.data.reason}` : ''}`;
    case 'resolved': return resolvedReasonLabel(event.data?.reason || null);
    default: return event.event_type.replace(/_/g, ' ');
  }
}

/** Activity worth showing (the internal "a suggestion was prepared" step is noise next to "found"). */
export const isVisibleActivity = (entry: ActivityEntry) => entry.event_type !== 'proposed';
